import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { BackupPanel } from "@/components/BackupPanel";
import { RecordsPrullenbak } from "@/components/RecordsPrullenbak";
import { RecordHistorieZoeker } from "@/components/RecordHistorie";
import { HulpStappen } from "@/components/HulpStappen";
import { Incidenten } from "@/components/Incidenten";

const fmt = (v) => (v ? new Date(v).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const COLS = ["Tijd", "Gebruiker", "Rol", "Module", "Actie", "Details"];
const rowValues = (l) => [fmt(l.timestamp), l.user_name || l.user_email || "", l.role || "", l.module || "", l.action || "", l.details || ""];

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [userFilter, setUserFilter] = useState("alle");

  useEffect(() => {
    api.get("/audit").then((r) => setLogs(r.data || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const users = useMemo(() => [...new Set(logs.map((l) => l.user_name).filter(Boolean))], [logs]);
  const rows = useMemo(() => logs.filter((l) => userFilter === "alle" || l.user_name === userFilter), [logs, userFilter]);

  const filterLabel = userFilter === "alle" ? "Alle gebruikers" : userFilter;

  const exportCSV = () => {
    if (rows.length === 0) return toast.error("Geen activiteit om te exporteren.");
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [COLS.map(esc).join(";"), ...rows.map((l) => rowValues(l).map(esc).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("Audit-log gedownload (CSV)");
  };

  const exportPDF = () => {
    if (rows.length === 0) return toast.error("Geen activiteit om te exporteren.");
    const style = `<style>body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}h1{font-size:20px;margin:0}table{border-collapse:collapse;width:100%;font-size:11px;margin-top:10px}th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:left;vertical-align:top}th{background:#f1f5f9}.meta{color:#64748b;font-size:11px;margin-bottom:8px}</style>`;
    const head = COLS.map((c) => `<th>${c}</th>`).join("");
    const body = rows.map((l) => `<tr>${rowValues(l).map((c) => `<td>${String(c).replace(/</g, "&lt;")}</td>`).join("")}</tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Sta pop-ups toe om het PDF-rapport te openen.");
    w.document.write(`<html><head><title>Audit log</title>${style}</head><body><div style="display:flex;align-items:center;gap:12px;border-bottom:2px solid #1C64B0;padding-bottom:8px;margin-bottom:10px"><img src="${window.location.origin}/clazing-logo.png" style="height:48px;width:auto;border:none;margin:0" onerror="this.style.display='none'"/><div><h1 style="margin:0">Audit log</h1><div style="font-size:11px;color:#1C64B0;font-weight:bold">Clazing Convenience Food B.V.</div></div></div><div class="meta">${new Date().toLocaleString("nl-NL")} &middot; Filter: ${filterLabel} &middot; ${rows.length} regels</div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table><scr` + `ipt>window.onload=function(){setTimeout(function(){window.print();},400);}</scr` + `ipt></body></html>`);
    w.document.close();
  };

  return (
    <div>
      <HulpStappen />
      <BackupPanel />
      <Incidenten />
      <RecordsPrullenbak />
      <RecordHistorieZoeker />
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Audit log</h1>
          <p className="text-sm text-slate-500 mt-1">Wie logde wanneer in en welke wijzigingen zijn gedaan. Alleen voor Bedrijfsleiding.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" data-testid="audit-export-csv" onClick={exportCSV}>
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" size="sm" data-testid="audit-export-pdf" onClick={exportPDF}>
            <FileText className="h-4 w-4 mr-2" /> PDF
          </Button>
          <div className="w-56">
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle gebruikers</SelectItem>
                {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {COLS.map((h) => (
                <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Geen activiteit gevonden.</TableCell></TableRow>
            ) : rows.map((l) => (
              <TableRow key={l.id} className="hover:bg-slate-50" data-testid="audit-row">
                <TableCell className="text-sm text-slate-600 tabular-nums whitespace-nowrap">{fmt(l.timestamp)}</TableCell>
                <TableCell className="text-sm font-semibold text-slate-800">{l.user_name || l.user_email}</TableCell>
                <TableCell className="text-sm text-slate-600">{l.role}</TableCell>
                <TableCell className="text-sm"><span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700">{l.module}</span></TableCell>
                <TableCell className="text-sm text-slate-600">{l.action}</TableCell>
                <TableCell className="text-sm text-slate-500">{l.details}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
